import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { CLASS_GROUPS, SUBJECTS_BY_CLASS } from "../data/education";

const FIELDS = [
  ["fees_per_hour", "Monthly Fee (Rs.) — for 1 hr/day", "number", "e.g. 2000"],
  ["experience",    "Experience (yrs)",    "number", "3"],
  ["city",          "City",                "text",   "Kanpur"],
  ["area",          "Area / Locality",     "text",   "e.g. Kidwai Nagar, Civil Lines, Kakadeo"],
  ["phone",         "WhatsApp Number",     "text",   "Enter 10-digit WhatsApp number"],
];

const CUR_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CUR_YEAR - 1989 }, (_, i) => CUR_YEAR - i); // current → 1990

const BASIC_CLASSES = [
  "Nursery","LKG","UKG",
  "Class 1","Class 2","Class 3","Class 4","Class 5",
  "Class 6","Class 7","Class 8"
];

const styles = `
  @keyframes fadeInUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
  }
  .dash-heading { transition: text-shadow 0.3s ease, color 0.3s ease; cursor:default; }
  .dash-heading:hover { text-shadow:0 0 20px #f9731688; color:#f97316 !important; }
  .stat-card { transition: border-color 0.25s, box-shadow 0.25s, transform 0.2s; }
  .stat-card:hover { border-color:#f9731655 !important; box-shadow:0 0 18px #f9731622; transform:translateY(-2px); }
  .glow-input:focus { border-color:#f97316 !important; box-shadow:0 0 0 3px #f9731622 !important; outline:none !important; }
  .orange-btn { transition: box-shadow 0.2s, transform 0.15s; }
  .orange-btn:hover { box-shadow:0 0 16px #f9731666; transform:translateY(-1px); }
  @keyframes pulse-green {
    0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.55); }
    50%       { box-shadow: 0 0 0 10px rgba(34,197,94,0); }
  }
  .go-live-btn {
    animation: pulse-green 1.8s ease-in-out infinite;
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .go-live-btn:hover {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 6px 28px rgba(34,197,94,0.55) !important;
    animation: none;
  }
  .ghost-btn { transition: border-color 0.2s, color 0.2s, box-shadow 0.2s; }
  .ghost-btn:hover { border-color:#f97316 !important; color:#f97316 !important; box-shadow:0 0 10px #f9731633; }
  .fade-in { animation: fadeInUp 0.4s ease both; }
  .toggle-wrap { transition: background 0.25s; }
  .chip-s { transition: all 0.15s ease; }
  .chip-s:hover { border-color:#f97316 !important; color:#f97316 !important; }
  .class-chip { transition: all 0.15s ease; }
  .class-chip:hover { border-color:#f97316 !important; color:#f97316 !important; }
  .db-stats-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin-bottom:16px; }
  .db-profile-header { display:flex; align-items:center; gap:16px; width:100%; }
  .db-save-row { display:flex; gap:10px; }
  @media(max-width:480px) {
    .db-stats-grid { grid-template-columns:repeat(2,1fr); }
    .db-profile-header { gap:10px; }
    .db-avatar { width:56px !important; height:56px !important; font-size:18px !important; }
    .db-photo-img { width:56px !important; height:56px !important; }
    .db-profile-name { font-size:15px !important; }
    .db-save-row { flex-direction:column; }
    .class-chip { padding:6px 12px !important; font-size:12px !important; }
    .chip-s { padding:5px 12px !important; font-size:12px !important; }
  }
`;

export default function Dashboard() {
  const { user } = useAuth();
  const fileRef  = useRef();
  const nav      = useNavigate();

  const [profile,   setProfile]   = useState(null);
  const [form,      setForm]      = useState({});
  const [teaching,  setTeaching]  = useState([]);
  const [editing,   setEditing]   = useState(false);
  const [errs,      setErrs]      = useState({});
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState("");
  const [uploading, setUploading] = useState(false);
  const [reviews,   setReviews]   = useState([]);
  const [referral,      setReferral]      = useState(null);
  const [refCopied,     setRefCopied]     = useState(false);
  const [leads,         setLeads]         = useState([]);
  const [fetchBanner,   setFetchBanner]   = useState(false);

  const [listingLoading, setListingLoading] = useState(false);
  const [relistLoading,  setRelistLoading]  = useState(false);

  // Mirror of backend tiers — used to display fee before order is created
  const getPlatformFee = (fph) => {
    const f = Number(fph) || 0;
    if (f <= 1500) return 99;
    if (f <= 2500) return 199;
    if (f <= 4000) return 299;
    return 499;
  };

  const isProfileComplete = profile &&
    profile.teaching?.length > 0 &&
    profile.fees_per_hour > 0 && profile.schedule_type &&
    profile.experience != null && profile.experience !== "" && profile.city &&
    profile.area && profile.phone && profile.bio &&
    profile.education?.school_10_name && profile.education?.school_10_board && profile.education?.school_10_year &&
    profile.education?.school_12_name && profile.education?.school_12_board && profile.education?.school_12_year;

  // ── Handle Cashfree redirect-back (UPI Intent / mobile fallback) ──────
  useEffect(() => {
    const params    = new URLSearchParams(window.location.search);
    const cfPayment = params.get("cf_payment");
    const cfOrderId = params.get("cf_order_id");
    if (!cfPayment || !cfOrderId) return;

    // Clear URL params immediately so re-load doesn't re-trigger
    window.history.replaceState({}, "", window.location.pathname);

    if (cfPayment === "listing") {
      api.post("/contacts/listing/verify", { order_id: cfOrderId })
        .then(({ data }) => {
          if (data.teacher) setProfile(p => ({ ...p, ...data.teacher }));
          setMsg("Profile listed! Parents can now find you.");
          // Reload full profile to get all fields
          api.get("/teachers/me/profile").then(r => setProfile(r.data)).catch(() => {});
        })
        .catch(e => setMsg(e.response?.data?.message || "Payment received but activation failed. Contact support."));
    }
  }, []);

  useEffect(() => {
    const endpoints = [
      ["/teachers/me/profile", "profile"],
      ["/teachers/me/reviews", "reviews"],
      ["/contacts/my-leads",   "leads"],
      ["/auth/referral",       "referral"],
    ];
    Promise.allSettled(endpoints.map(([ep]) => api.get(ep))).then(results => {
      let anyFailed = false;
      results.forEach((r) => { if (r.status === "rejected") { anyFailed = true; } });
      if (anyFailed) setFetchBanner(true);

      const [profileRes, reviewsRes, leadsRes, referralRes] = results;
      if (profileRes.status === "fulfilled") {
        const d = profileRes.value.data;
        setProfile(d); setForm({ ...d });
        if (d.teaching?.length > 0) setTeaching(d.teaching);
        else if (d.classes?.length > 0) setTeaching(d.classes.map(c => ({ class: c, subjects: d.subjects?.length > 0 ? d.subjects : ["All"] })));
      }
      if (reviewsRes.status  === "fulfilled") setReviews(reviewsRes.value.data);
      if (leadsRes.status    === "fulfilled") setLeads(leadsRes.value.data);
      if (referralRes.status === "fulfilled") setReferral(referralRes.value.data);
    });
  }, []);

  const showMsg = (text) => { setMsg(text); setTimeout(() => setMsg(""), 3500); };


  // ── Pay platform fee to relist after demo ─────────────
  const payToRelist = async () => {
    setRelistLoading(true);
    try {
      const returnUrl = `${window.location.origin}/dashboard?cf_payment=listing`;
      const { data: order } = await api.post("/contacts/listing/order", { return_url: returnUrl });
      const { load }  = await import("@cashfreepayments/cashfree-js");
      const cfMode    = process.env.NODE_ENV === "production" ? "production" : "sandbox";
      const cashfree  = await load({ mode: cfMode });
      const result    = await cashfree.checkout({ paymentSessionId: order.payment_session_id, redirectTarget: "_modal" });
      if (result.paymentDetails) {
        try {
          await api.post("/contacts/listing/verify", { order_id: order.order_id });
          const { data: fresh } = await api.get("/teachers/me/profile");
          setProfile(fresh);
          showMsg("Profile listed again! Parents can now find you.");
        } catch {
          showMsg("Payment done but verification failed. Contact support.");
        }
      } else if (result.error) {
        showMsg("Payment cancelled or failed. Try again.");
      }
    } catch (e) {
      showMsg(e.response?.data?.message || "Something went wrong. Try again.");
    }
    setRelistLoading(false);
  };

  // ── Go live (auto-list free) then browse search ────────
  const goLiveAndSearch = async () => {
    if (!profile.listing_active) {
      // Not listed yet — auto-list free, then go to search
      setListingLoading(true);
      try {
        const { data } = await api.post("/contacts/listing/free");
        setProfile(data.teacher);
      } catch (e) {
        // If free listing exhausted or error, still go to search
        showMsg(e.response?.data?.message || "Could not auto-list. Please try again.");
        setListingLoading(false);
        return;
      }
      setListingLoading(false);
    }
    nav("/search");
  };

  // ── Teaching matrix helpers ────────────────────────────
  const addClass    = (c) => { if (!teaching.find(t => t.class === c)) setTeaching(p => [...p, { class:c, subjects:["All"] }]); };
  const removeClass = (c) => setTeaching(p => p.filter(t => t.class !== c));
  const setAllSubjects = (c) => setTeaching(p => p.map(t => t.class===c ? {...t, subjects:["All"]} : t));
  const setSpecificSubjects = (c) => setTeaching(p => p.map(t => t.class===c ? {...t, subjects:[]} : t));
  const toggleSubjectForClass = (c, s) => setTeaching(p => p.map(t => {
    if (t.class !== c) return t;
    const cur = t.subjects.filter(x => x !== "All");
    return { ...t, subjects: cur.includes(s) ? cur.filter(x=>x!==s) : [...cur,s] };
  }));

  // ── Photo upload ───────────────────────────────────────
  const handlePhotoChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setMsg("Please select an image file (JPG, PNG, etc.)"); return; }
    if (file.size > 5*1024*1024) { setMsg("Photo must be under 5MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("photo", file);
    try {
      const { data } = await api.post("/teachers/me/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setProfile(p => ({ ...p, photo_url: data.photo_url }));
      showMsg("Photo uploaded!");
    } catch (e) {
      setMsg("Upload failed: " + (e.response?.data?.message || e.message));
    }
    setUploading(false);
  };

  // ── Validate ───────────────────────────────────────────
  const PHONE_REGEX = /^[6-9]\d{9}$/;
  const validate = () => {
    const e = {};
    FIELDS.forEach(([k,l]) => {
      if (!form[k] || !String(form[k]).trim()) e[k] = l.split(" (")[0] + " is required";
    });
    if (!form.schedule_type) e.schedule_type = "Schedule type is required";
    if (form.phone && !PHONE_REGEX.test(form.phone))
      e.phone = "Enter valid 10-digit mobile number starting with 6, 7, 8, or 9";
    if (!teaching.length) e.teaching = "Add at least one class";
    if (teaching.some(t => t.subjects.length === 0)) e.teaching = "Select subjects for all classes";
    if (!form.bio?.trim()) e.bio = "Bio is required";
    if (!form.education?.school_10_name?.trim()) e.school_10_name = "Class 10 school name is required";
    if (!form.education?.school_10_board)        e.school_10_board = "Class 10 board is required";
    if (!form.education?.school_10_year)         e.school_10_year = "Class 10 passing year is required";
    if (!form.education?.school_12_name?.trim()) e.school_12_name = "Class 12 school name is required";
    if (!form.education?.school_12_board)        e.school_12_board = "Class 12 board is required";
    if (!form.education?.school_12_year)         e.school_12_year = "Class 12 passing year is required";
    return e;
  };

  // ── Save profile ───────────────────────────────────────
  const save = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrs(e); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/teachers/me/profile", {
        ...form, teaching,
        fees_per_hour: +form.fees_per_hour, schedule_type: form.schedule_type, experience: +form.experience,
        education: form.education || {},
      });
      setProfile(data);
      setEditing(false);
      setErrs({});
      showMsg("Profile saved!");
    } catch (e) {
      setErrs({ bio: e.response?.data?.message || "Save failed" });
    }
    setSaving(false);
  };

  const toggleAvail = async () => {
    try {
      const { data } = await api.patch("/teachers/me/available", { available: !profile.available });
      setProfile(p => ({ ...p, available: data.available }));
    } catch (e) {
      showMsg("Failed to update availability. Try again.");
    }
  };

  const handleReview = async (reviewId, status) => {
    try {
      const { data } = await api.patch("/teachers/me/reviews/" + reviewId, { status });
      setReviews(rs => rs.map(r => r._id===reviewId ? {...r, status:data.status} : r));
      showMsg(status==="approved" ? "Review approved!" : "Review rejected.");
    } catch (e) {
      setMsg("Failed: " + (e.response?.data?.message || e.message));
    }
  };

  const inp = k => ({
    width:"100%", marginTop:6, padding:"10px 12px", borderRadius:8,
    border:"1px solid "+(errs[k]?"#ef4444":"#1e293b"),
    background:"#0a0a0a", color:"#f1f5f9", fontSize:13,
    boxSizing:"border-box", fontFamily:"inherit",
    transition:"border-color .2s, box-shadow .2s"
  });

  const initials = (user?.name||"T").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();

  if (!profile) return (
    <div style={{ textAlign:"center", padding:60, color:"#e2e8f0", background:"#0a0a0a", minHeight:"100vh" }}>
      <style>{styles}</style>
      <div style={{ fontSize:32, marginBottom:12 }}>⏳</div>
      Loading dashboard...
    </div>
  );

  const pendingReviews  = reviews.filter(r => r.status==="pending");
  const approvedReviews = reviews.filter(r => r.status==="approved");

  return (
    <div style={{ background:"#0a0a0a", minHeight:"100vh", padding:"16px 14px 40px", color:"#f1f5f9", maxWidth:640, margin:"0 auto", boxSizing:"border-box", width:"100%" }}>
      <Helmet>
        <title>Teacher Dashboard | PadhaiPass</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <style>{styles}</style>


      {fetchBanner && (
        <div className="fade-in" style={{ background:"#1a1000", border:"1px solid #f97316AA", color:"#fbbf24", padding:"10px 16px", borderRadius:10, marginBottom:16, fontSize:13, display:"flex", alignItems:"center", gap:10 }}>
          <span>⚠️</span>
          <span style={{ flex:1 }}>Some data failed to load. Showing partial info — <button onClick={() => window.location.reload()} style={{ background:"none", border:"none", color:"#f97316", cursor:"pointer", fontWeight:700, fontSize:13, padding:0 }}>refresh</button> to retry.</span>
        </div>
      )}

      {msg && (
        <div className="fade-in" style={{ background:"#22c55e15", border:"1px solid #22c55e44", color:"#22c55e", padding:"10px 16px", borderRadius:10, marginBottom:16, fontSize:14 }}>
          ✅ {msg}
        </div>
      )}

      {/* ── Profile header ── */}
      <div className="fade-in" style={{ marginBottom:22, background:"#111111", borderRadius:16, padding:16, border:"1px solid #1e293b" }}>
       <div className="db-profile-header">
        <div style={{ position:"relative", flexShrink:0 }}>
          {profile.photo_url ? (
            <img className="db-photo-img" src={profile.photo_url} alt="Profile" style={{ width:72, height:72, borderRadius:16, objectFit:"cover", border:"2px solid #f9731444" }} />
          ) : (
            <div className="db-avatar" style={{ width:72, height:72, borderRadius:16, background:"linear-gradient(135deg,#f97316,#ea580c)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:24 }}>
              {initials}
            </div>
          )}
          <div onClick={() => fileRef.current?.click()}
            style={{ position:"absolute", bottom:-4, right:-4, width:26, height:26, background:"#f97316", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", border:"2px solid #0a0a0a", fontSize:13 }}>
            📷
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handlePhotoChange} />
        </div>
        <div style={{ flex:1 }}>
          <div className="dash-heading" style={{ fontWeight:700, fontSize:18, color:"#f1f5f9" }}>{user?.name}</div>
          <div style={{ fontSize:13, color:"#e2e8f0", marginTop:2 }}>Teacher Dashboard</div>
          <div onClick={() => fileRef.current?.click()} style={{ fontSize:12, color:"#f97316", cursor:"pointer", marginTop:6, fontWeight:500 }}>
            {uploading ? "Uploading..." : profile.photo_url ? "Change photo" : "Upload photo"}
          </div>
        </div>
        {/* Availability toggle — right side of header */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, flexShrink:0 }}>
          <div onClick={toggleAvail} className="toggle-wrap"
            style={{ width:50, height:28, borderRadius:14, background:profile.available?"#f97316":"#1e293b", cursor:"pointer", position:"relative", border:"1px solid "+(profile.available?"#f97316":"#334155") }}>
            <div style={{ width:22, height:22, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:profile.available?25:3, transition:"left .2s" }} />
          </div>
          <div style={{ fontSize:10, color: profile.available?"#f97316":"#475569", fontWeight:600, textAlign:"center" }}>
            {profile.available ? "Open" : "Busy"}
          </div>
        </div>
       </div>
      </div>

      {/* ── Demo Requests — shown ONLY when teacher already relisted (historical view) ── */}
      {!profile.demo_awaiting_payment && leads.length > 0 && (
        <div className="fade-in" style={{ background:"#052e16", borderRadius:14, padding:16, marginBottom:14, border:"1px solid #22c55e55", boxShadow:"0 0 18px #22c55e22" }}>
          <div style={{ fontWeight:800, color:"#22c55e", marginBottom:4, fontSize:16 }}>
            🎯 Past Demo Requests ({leads.length})
          </div>
          <div style={{ fontSize:12, color:"#4ade80", marginBottom:14 }}>
            Parents who contacted you for a free demo class
          </div>
          {leads.map(lead => (
            <div key={lead._id} style={{ background:"#0a1a0f", borderRadius:10, padding:"14px 16px", marginBottom:10, border:"1px solid #22c55e33" }}>
              {[
                ["Parent's Name",   lead.student_name  || "—"],
                ["Parent's Number", lead.student_phone || "—"],
                ["Child's Class",   lead.student_class || "—"],
              ].map(([label, value]) => (
                <div key={label} style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:6 }}>
                  <span style={{ fontSize:11, color:"#4ade80", minWidth:110, flexShrink:0 }}>{label} →</span>
                  <span style={{ fontSize:13, fontWeight:600, color:"#f1f5f9" }}>{value}</span>
                </div>
              ))}
              <div style={{ fontSize:10, color:"#4ade8066", marginTop:6 }}>
                {new Date(lead.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── LISTING STATUS ── */}
      {profile.listing_active ? (
        /* Active listing */
        <div className="fade-in" style={{ background:"#f9731608", border:"1px solid #f9731633", borderRadius:16, padding:16, marginBottom:16 }}>
          <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:12 }}>
            <span style={{ fontSize:28 }}>🔶</span>
            <div>
              <div className="dash-heading" style={{ fontWeight:700, color:"#f97316" }}>Listed in Search</div>
              <div style={{ fontSize:13, color:"#e2e8f0" }}>Students can find and connect with you</div>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:0 }}>
            <div style={{ background:"#111111", borderRadius:10, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:13, color:"#e2e8f0" }}>Connections</div>
              <div style={{ fontSize:18, fontWeight:800, color:"#f97316" }}>{profile.total_connections||0}</div>
            </div>
            <div style={{ background:"#111111", borderRadius:10, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:13, color:"#e2e8f0" }}>Leads</div>
              <div style={{ fontSize:18, fontWeight:800, color:"#f97316" }}>{leads.length}</div>
            </div>
          </div>
          <button onClick={() => nav("/teacher/" + profile._id)} className="orange-btn"
            style={{ width:"100%", marginTop:12, padding:11, background:"#f97316", color:"#fff", border:"none", borderRadius:10, fontSize:14, cursor:"pointer", fontWeight:700 }}>
            🏠 View Your Profile
          </button>
        </div>
      ) : profile.demo_awaiting_payment ? (
        /* ── Demo confirmed → pay to relist ── */
        <div className="fade-in" style={{
          borderRadius:18, marginBottom:16, overflow:"hidden",
          border:"1px solid #f9731650",
          boxShadow:"0 0 0 1px #f9731618, 0 8px 32px #f9731622",
          background:"#0f0a06",
        }}>

          {/* ── Top status pill row ── */}
          <div style={{ padding:"14px 18px 0", display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#f97316", boxShadow:"0 0 8px #f97316" }} />
            <span style={{ fontSize:11, fontWeight:700, color:"#f97316", textTransform:"uppercase", letterSpacing:1.2 }}>
              Demo Confirmed · Profile Paused
            </span>
          </div>

          {/* ── Headline ── */}
          <div style={{ padding:"12px 18px 14px" }}>
            <div style={{ fontSize:20, fontWeight:900, color:"#ffffff", lineHeight:1.25, letterSpacing:-0.3 }}>
              {leads.length > 0 && leads[0].student_name
                ? <>{leads[0].student_name} <span style={{ color:"#f97316" }}>confirmed</span> a demo with you</>
                : <>A parent <span style={{ color:"#f97316" }}>confirmed</span> a demo with you</>}
            </div>
            <div style={{ fontSize:14, color:"#cbd5e1", marginTop:7, lineHeight:1.55, fontWeight:500 }}>
              Profile auto-paused on demo confirmation. Give the class — pay once — go live again.
            </div>
          </div>

          {/* ── Student card ── */}
          {leads.length > 0 && (
            <div style={{ margin:"0 14px 14px", borderRadius:12, overflow:"hidden", border:"1px solid #1e293b", background:"#0a0a0a" }}>
              <div style={{ padding:"10px 14px", background:"#111111", borderBottom:"1px solid #1e293b", display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:30, height:30, borderRadius:8, background:"linear-gradient(135deg,#f97316,#ea580c)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>
                  👤
                </div>
                <div>
                  <div style={{ fontSize:14, fontWeight:800, color:"#f1f5f9" }}>{leads[0].student_name || "Parent"}</div>
                  <div style={{ fontSize:11, color:"#475569" }}>
                    Confirmed {new Date(leads[0].createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                  </div>
                </div>
              </div>
              <div style={{ padding:"12px 14px" }}>
                {[
                  ["📞 WhatsApp", leads[0].student_phone || "—"],
                  ["📚 Child's Class", leads[0].student_class || "—"],
                ].map(([label, value]) => (
                  <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:"1px solid #1a1a1a" }}>
                    <span style={{ fontSize:12, color:"#475569", fontWeight:500 }}>{label}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:"#f1f5f9" }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Incomplete profile warning — blocks payment ── */}
          {!isProfileComplete ? (
            <div style={{ margin:"0 14px 16px", borderRadius:12, border:"1px solid rgba(251,146,60,0.4)", background:"rgba(249,115,22,0.06)", padding:"14px 16px" }}>
              <div style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:12 }}>
                <span style={{ fontSize:20, flexShrink:0 }}>⚠️</span>
                <div>
                  <div style={{ fontSize:14, fontWeight:800, color:"#fb923c", marginBottom:4 }}>
                    Profile incomplete — complete it before going live
                  </div>
                  <div style={{ fontSize:12, color:"#94a3b8", lineHeight:1.6 }}>
                    Demo confirm hua — ab profile complete karo (fees, subjects, area) taaki relist ke baad students contact kar sakein.
                  </div>
                </div>
              </div>
              {[
                !profile.fees_per_hour     && "Monthly fee set karo",
                !(profile.teaching?.length > 0) && "Subjects / classes add karo",
                !profile.area              && "Area / locality fill karo",
                !profile.phone             && "WhatsApp number add karo",
                !profile.bio               && "Bio likho",
              ].filter(Boolean).map(item => (
                <div key={item} style={{ display:"flex", gap:8, alignItems:"center", marginBottom:5 }}>
                  <span style={{ width:5, height:5, borderRadius:"50%", background:"#f97316", flexShrink:0, display:"inline-block" }} />
                  <span style={{ fontSize:12, color:"#cbd5e1" }}>{item}</span>
                </div>
              ))}
              <button
                onClick={() => document.getElementById("profile-edit-section")?.scrollIntoView({ behavior:"smooth" })}
                style={{ marginTop:12, width:"100%", padding:"11px 0", background:"linear-gradient(135deg,#f97316,#ea580c)", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                ✏️ Complete Profile Now →
              </button>
            </div>
          ) : (
            <>
              {/* ── What happens after payment ── */}
              <div style={{ margin:"0 14px", borderRadius:12, background:"#111111", border:"1px solid #1e293b", padding:"12px 14px", marginBottom:14 }}>
                <div style={{ fontSize:13, color:"#e2e8f0", fontWeight:700, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>
                  After you pay ₹{getPlatformFee(profile.fees_per_hour)}
                </div>
                {[
                  ["🟢", "Profile goes live instantly"],
                  ["🔍", "You appear in search results again"],
                  ["📩", "New demo requests start coming in"],
                ].map(([icon, text]) => (
                  <div key={text} style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8 }}>
                    <span style={{ fontSize:14 }}>{icon}</span>
                    <span style={{ fontSize:13, color:"#cbd5e1" }}>{text}</span>
                  </div>
                ))}
              </div>

              {/* ── Pay button ── */}
              <div style={{ padding:"0 14px 16px" }}>
                <button
                  onClick={payToRelist}
                  disabled={relistLoading}
                  className="orange-btn"
                  style={{
                    width:"100%", padding:"16px 0",
                    background: relistLoading ? "#7c2d12" : "linear-gradient(135deg,#f97316 0%,#ea580c 100%)",
                    color:"#fff", border:"none", borderRadius:12,
                    fontSize:16, cursor: relistLoading ? "not-allowed" : "pointer",
                    fontWeight:800, letterSpacing:0.2,
                    boxShadow: relistLoading ? "none" : "0 4px 24px #f9731650",
                    transition:"box-shadow .2s, transform .15s",
                  }}>
                  {relistLoading ? "⏳ Processing payment…" : `💳  Pay ₹${getPlatformFee(profile.fees_per_hour)} & Go Live Again`}
                </button>
                <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:16, marginTop:10 }}>
                  <span style={{ fontSize:11, color:"#334155" }}>🔒 Secured by Cashfree</span>
                  <span style={{ fontSize:11, color:"#334155" }}>⚡ Instant activation</span>
                </div>
              </div>
            </>
          )}
        </div>

      ) : (
        /* Not listed */
        <div className="fade-in" style={{ borderRadius:16, marginBottom:16, overflow:"hidden", border: isProfileComplete ? "1px solid #22c55e55" : "1px solid #f9731633" }}>

          {/* Top banner */}
          <div style={{ background: isProfileComplete ? "#22c55e18" : "#f9731610", padding:"14px 18px", display:"flex", gap:12, alignItems:"center" }}>
            <span style={{ fontSize:26 }}>{isProfileComplete ? "🟢" : "🔒"}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:15, color: isProfileComplete ? "#22c55e" : "#f97316" }}>
                {profile.last_student_name
                  ? `${profile.last_student_name} wants to connect with you!`
                  : profile.total_connections > 0
                    ? "Students connected before — get listed again!"
                    : isProfileComplete
                      ? "Profile complete! You're ready to go live"
                      : "Profile incomplete — not visible to students"
                }
              </div>
              <div style={{ fontSize:12, color:"#94a3b8", marginTop:3 }}>
                {isProfileComplete
                  ? "Tap the button below — students are searching right now"
                  : "Fill all required fields below, then come back here to go live"
                }
              </div>
            </div>
          </div>

          {/* CTA area */}
          <div style={{ background:"#0a0a0a", padding:"16px 18px" }}>
            {!isProfileComplete ? (
              <>
                <div style={{ fontSize:13, color:"#f59e0b", background:"#f59e0b12", border:"1px solid #f59e0b30", borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
                  ⚠️ Complete all required fields below to unlock listing
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={goLiveAndSearch}
                  disabled={listingLoading}
                  className="go-live-btn"
                  style={{
                    width:"100%", padding:"15px 0",
                    background: listingLoading ? "#16a34a" : "#22c55e",
                    color:"#fff", border:"none", borderRadius:10,
                    fontSize:16, cursor: listingLoading ? "not-allowed" : "pointer",
                    fontWeight:800, letterSpacing:0.3,
                    opacity: listingLoading ? 0.8 : 1,
                  }}>
                  {listingLoading ? "⏳ Going Live..." : "🚀 Go Live — List My Profile FREE"}
                </button>
                <div style={{ fontSize:11, color:"#475569", textAlign:"center", marginTop:8 }}>
                  You'll appear in student searches instantly after clicking
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Profile view / edit ── */}
      {!editing ? (
        <div className="fade-in" style={{ background:"#111111", borderRadius:14, padding:16, marginBottom:14, border:"1px solid #1e293b" }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:14 }}>
            <div className="dash-heading" style={{ fontWeight:700, color:"#f97316", fontSize:15 }}>Your Profile</div>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
              <button onClick={() => { setForm({...profile}); setTeaching(profile.teaching?.length>0?profile.teaching:[]); setEditing(true); }}
                className="ghost-btn"
                style={{ background:"transparent", border:"1px solid #f9731655", color:"#64748b", borderRadius:8, padding:"5px 14px", fontSize:12, cursor:"pointer" }}>
                <span style={{ fontWeight:800, color:"#ffffff" }}>{isProfileComplete ? "Edit Profile ✏️" : "Complete Profile ✏️"}</span>
              </button>
              {!isProfileComplete && <span style={{ fontSize:11, color:"#f97316", fontWeight:600 }}>👆 Click here to complete profile</span>}
            </div>
          </div>

          {profile.teaching?.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:14, color:"#ffffff", fontWeight:800, textTransform:"none", letterSpacing:0, marginBottom:8 }}>What you teach</div>
              {profile.teaching.map((t,i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid #1e293b", fontSize:13 }}>
                  <span style={{ color:"#f97316", fontWeight:600, flexShrink:0, minWidth:100 }}>{t.class}</span>
                  <span style={{ color:"#f1f5f9", textAlign:"right", maxWidth:"60%", wordBreak:"break-word" }}>
                    {t.subjects.includes("All") ? "All Subjects" : t.subjects.join(", ")}
                  </span>
                </div>
              ))}
            </div>
          )}

          {[
            ["Fee/mo",      profile.fees_per_hour ? "₹"+profile.fees_per_hour+"/mo" : "—"],
            ["Schedule",    profile.schedule_type === "regular" ? "Regular" : profile.schedule_type === "alternate" ? "Alternate" : "—"],
            ["Demo Class",  profile.offers_demo_class === false ? "No" : "Yes ✓"],
            ["Experience",  (profile.experience != null && profile.experience !== "") ? profile.experience+" years" : "—"],
            ["City",        profile.city||"—"],
            ["Area",        profile.area||"—"],
            ["WhatsApp",    profile.phone||"—"],
            ["Bio",         profile.bio||"—"],
          ].map(([l,v]) => (
            <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #1e293b", fontSize:13 }}>
              <span style={{ color:"#e2e8f0", flexShrink:0, minWidth:80 }}>{l}</span>
              <span style={{ color:"#f1f5f9", textAlign:"right", maxWidth:"65%", wordBreak:"break-word" }}>{v}</span>
            </div>
          ))}
        </div>

      ) : (

        <div id="profile-edit-section" className="fade-in" style={{ background:"#111111", borderRadius:14, padding:16, marginBottom:14, border:"1px solid #1e293b" }}>
          <div className="dash-heading" style={{ fontWeight:900, color:"#ffffff", marginBottom:4, fontSize:18 }}>{isProfileComplete ? "Edit Profile" : "Complete Profile"}</div>

          <p style={{ fontSize:12, color:"#ef4444", marginBottom:16 }}>All fields are mandatory *</p>

          {/* Teaching Matrix */}
          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:14, color:"#ffffff", fontWeight:700 }}>
              What do you teach? * <span style={{ color:"#a1a1aa", fontWeight:400 }}>(select class then subjects)</span>
            </label>
            {errs.teaching && <div style={{ color:"#ef4444", fontSize:11, marginTop:4, marginBottom:8 }}>{errs.teaching}</div>}

            <div style={{ marginTop:10, marginBottom:14 }}>
              <div style={{ fontSize:14, fontWeight:800, color:"#ffffff", letterSpacing:0, textTransform:"none", marginBottom:10 }}>
                Step 1 — Select Classes
              </div>
              {CLASS_GROUPS.map(g => (
                <div key={g.group} style={{ marginBottom:12 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0", letterSpacing:0, textTransform:"none", marginBottom:6 }}>{g.group}</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                    {g.classes.map(c => {
                      const sel = teaching.find(t => t.class===c);
                      return (
                        <div key={c} onClick={() => sel ? removeClass(c) : addClass(c)} className="class-chip"
                          style={{ padding:"7px 16px", borderRadius:20, fontSize:13, cursor:"pointer",
                            border:"1px solid "+(sel?"#f97316":"#334155"),
                            background:sel?"#f97316":"#1a1a2e",
                            color:sel?"#0a0a0a":"#e2e8f0", fontWeight:sel?700:500 }}>
                          {sel?"✓ ":""}{c}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {teaching.length > 0 && (
              <div>
                <div style={{ fontSize:14, fontWeight:800, color:"#ffffff", letterSpacing:0, textTransform:"none", marginBottom:10 }}>
                  Step 2 — Subjects for each class
                </div>
                {teaching.map(entry => {
                  const isBasic = BASIC_CLASSES.includes(entry.class);
                  const isAll   = entry.subjects.includes("All");
                  const subs    = SUBJECTS_BY_CLASS[entry.class] || [];
                  return (
                    <div key={entry.class} style={{ background:"#0a0a0a", borderRadius:12, padding:14, marginBottom:10, border:"1px solid #1e293b" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                        <div style={{ fontWeight:800, fontSize:15, color:"#f97316" }}>{entry.class}</div>
                        <div onClick={() => removeClass(entry.class)}
                          style={{ fontSize:11, color:"#ef4444", cursor:"pointer", padding:"2px 8px", borderRadius:6, border:"1px solid #ef444433", background:"#ef444415" }}>
                          Remove
                        </div>
                      </div>
                      {isBasic && (
                        <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                          <div onClick={() => setAllSubjects(entry.class)}
                            style={{ flex:1, padding:"9px 0", textAlign:"center", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:700,
                              background:isAll?"#22c55e":"#1a1a2e", color:isAll?"#0a0a0a":"#e2e8f0", border:"1px solid "+(isAll?"#22c55e":"#334155") }}>
                            ✓ All Subjects
                          </div>
                          <div onClick={() => setSpecificSubjects(entry.class)}
                            style={{ flex:1, padding:"9px 0", textAlign:"center", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:700,
                              background:!isAll?"#f97316":"#1a1a2e", color:!isAll?"#0a0a0a":"#e2e8f0", border:"1px solid "+(!isAll?"#f97316":"#334155") }}>
                            Specific Subjects
                          </div>
                        </div>
                      )}
                      {(!isAll || !isBasic) && subs.length > 0 && (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                          {subs.map(s => {
                            const sel = entry.subjects.includes(s);
                            return (
                              <div key={s} onClick={() => toggleSubjectForClass(entry.class,s)} className="chip-s"
                                style={{ padding:"6px 14px", borderRadius:20, fontSize:13, cursor:"pointer",
                                  border:"1px solid "+(sel?"#f97316":"#334155"),
                                  background:sel?"#f97316":"#1a1a2e", color:sel?"#0a0a0a":"#e2e8f0", fontWeight:sel?700:500 }}>
                                {s}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {!isAll && entry.subjects.length===0 && (
                        <div style={{ fontSize:11, color:"#f59e0b", marginTop:6 }}>⚠️ Select at least one subject</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {FIELDS.map(([k,l,type,ph]) => (
            <div key={k} style={{ marginBottom:12 }}>
              <label style={{ fontSize:13, color:"#ffffff", fontWeight:600 }}>{l} * {k === "area" && <span style={{ color:"#64748b", fontWeight:400, fontSize:11 }}>Write all areas where you go for teaching, separated by comma</span>}</label>
              <input
                className="glow-input"
                type={type}
                style={inp(k)}
                placeholder={ph}
                inputMode={k === "phone" ? "numeric" : undefined}
                maxLength={k === "phone" ? 10 : undefined}
                value={form[k]||""}
                onChange={e => {
                  const val = k === "phone"
                    ? e.target.value.replace(/\D/g, "").slice(0, 10)
                    : e.target.value;
                  setForm(f => ({ ...f, [k]: val }));
                }}
              />
              {errs[k] && <div style={{ color:"#ef4444", fontSize:11, marginTop:3 }}>{errs[k]}</div>}

              {/* Schedule type + demo class toggle — rendered right after fees_per_hour */}
              {k === "fees_per_hour" && (
                <>
                  <div style={{ marginTop:12 }}>
                    <label style={{ fontSize:13, color:"#ffffff", fontWeight:600 }}>Schedule Type *</label>
                    <select
                      className="glow-input"
                      style={inp("schedule_type")}
                      value={form.schedule_type || ""}
                      onChange={e => setForm(f => ({ ...f, schedule_type: e.target.value }))}
                    >
                      <option value="">Select schedule type</option>
                      <option value="regular">Regular (fixed days every week)</option>
                      <option value="alternate">Alternate (every alternate day)</option>
                    </select>
                    {errs.schedule_type && <div style={{ color:"#ef4444", fontSize:11, marginTop:3 }}>{errs.schedule_type}</div>}
                  </div>

                  <div style={{ marginTop:12 }}>
                    <label style={{ fontSize:13, color:"#ffffff", fontWeight:600 }}>Do you offer a free demo class? *</label>
                    <div style={{ display:"flex", gap:8, marginTop:8 }}>
                      <div onClick={() => setForm(f => ({ ...f, offers_demo_class: true }))}
                        style={{ flex:1, padding:"10px 0", textAlign:"center", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:700,
                          background: form.offers_demo_class !== false ? "#22c55e" : "#1a1a2e",
                          color:      form.offers_demo_class !== false ? "#0a0a0a" : "#e2e8f0",
                          border:    `1px solid ${form.offers_demo_class !== false ? "#22c55e" : "#334155"}`,
                          transition:"all .15s" }}>
                        ✓ Yes — Free Demo
                      </div>
                      <div onClick={() => setForm(f => ({ ...f, offers_demo_class: false }))}
                        style={{ flex:1, padding:"10px 0", textAlign:"center", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:700,
                          background: form.offers_demo_class === false ? "#ef4444" : "#1a1a2e",
                          color:      form.offers_demo_class === false ? "#fff"    : "#e2e8f0",
                          border:    `1px solid ${form.offers_demo_class === false ? "#ef4444" : "#334155"}`,
                          transition:"all .15s" }}>
                        No Demo
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}

          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:13, color:"#ffffff", fontWeight:600 }}>Bio * <span style={{ color:"#a1a1aa", fontWeight:400 }}>(tell students about yourself)</span></label>
            <textarea className="glow-input"
              style={{ ...inp("bio"), height:90, resize:"none", marginTop:6 }}
              placeholder="E.g. I am a B.Tech graduate with 5 years of teaching experience..."
              value={form.bio||""} onChange={e => setForm(f=>({...f,bio:e.target.value}))} />
            {errs.bio && <div style={{ color:"#ef4444", fontSize:11, marginTop:3 }}>{errs.bio}</div>}
          </div>

          {/* ── Education Section ── */}
          <div style={{ borderTop:"1px solid #1e293b", paddingTop:16, marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#f97316", marginBottom:14 }}>🎓 Education <span style={{ color:"#64748b", fontSize:12, fontWeight:400 }}>(helps parents trust you)</span></div>

            {/* Class 10 */}
            <div style={{ fontSize:12, color:"#94a3b8", fontWeight:600, marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>
              Class 10 <span style={{ color:"#ef4444" }}>*</span>
            </div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:4 }}>
              <input className="glow-input" style={{ ...inp("school_10_name"), flex:2, minWidth:140 }}
                placeholder="School Name *"
                value={form.education?.school_10_name||""}
                onChange={e => setForm(f => ({ ...f, education: { ...f.education, school_10_name: e.target.value } }))} />
              <select className="glow-input" style={{ ...inp("school_10_board"), flex:1, minWidth:120 }}
                value={form.education?.school_10_board||""}
                onChange={e => setForm(f => ({ ...f, education: { ...f.education, school_10_board: e.target.value } }))}>
                <option value="">Board *</option>
                {["CBSE","ICSE","UP Board","State Board","Other"].map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <select className="glow-input" style={{ ...inp("school_10_year"), width:100 }}
                value={form.education?.school_10_year||""}
                onChange={e => setForm(f => ({ ...f, education: { ...f.education, school_10_year: e.target.value } }))}>
                <option value="">Year *</option>
                {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {errs.school_10_name  && <div style={{ color:"#ef4444", fontSize:11, marginBottom:4 }}>{errs.school_10_name}</div>}
            {errs.school_10_board && <div style={{ color:"#ef4444", fontSize:11, marginBottom:4 }}>{errs.school_10_board}</div>}
            {errs.school_10_year  && <div style={{ color:"#ef4444", fontSize:11, marginBottom:8 }}>{errs.school_10_year}</div>}

            {/* Class 12 */}
            <div style={{ fontSize:12, color:"#94a3b8", fontWeight:600, marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>
              Class 12 <span style={{ color:"#ef4444" }}>*</span>
            </div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:4 }}>
              <input className="glow-input" style={{ ...inp("school_12_name"), flex:2, minWidth:140 }}
                placeholder="School Name *"
                value={form.education?.school_12_name||""}
                onChange={e => setForm(f => ({ ...f, education: { ...f.education, school_12_name: e.target.value } }))} />
              <select className="glow-input" style={{ ...inp("school_12_board"), flex:1, minWidth:120 }}
                value={form.education?.school_12_board||""}
                onChange={e => setForm(f => ({ ...f, education: { ...f.education, school_12_board: e.target.value } }))}>
                <option value="">Board *</option>
                {["CBSE","ICSE","UP Board","State Board","Other"].map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <select className="glow-input" style={{ ...inp("school_12_year"), width:100 }}
                value={form.education?.school_12_year||""}
                onChange={e => setForm(f => ({ ...f, education: { ...f.education, school_12_year: e.target.value } }))}>
                <option value="">Year *</option>
                {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {errs.school_12_name  && <div style={{ color:"#ef4444", fontSize:11, marginBottom:4 }}>{errs.school_12_name}</div>}
            {errs.school_12_board && <div style={{ color:"#ef4444", fontSize:11, marginBottom:4 }}>{errs.school_12_board}</div>}
            {errs.school_12_year  && <div style={{ color:"#ef4444", fontSize:11, marginBottom:8 }}>{errs.school_12_year}</div>}

            {/* Graduation */}
            <div style={{ fontSize:12, color:"#94a3b8", fontWeight:600, marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>Graduation</div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:10 }}>
              <input className="glow-input" style={{ ...inp(), flex:1, minWidth:120 }}
                placeholder="Degree (B.Sc, B.Tech…)"
                value={form.education?.degree_name||""}
                onChange={e => setForm(f => ({ ...f, education: { ...f.education, degree_name: e.target.value } }))} />
              <input className="glow-input" style={{ ...inp(), flex:2, minWidth:140 }}
                placeholder="College Name"
                value={form.education?.college_name||""}
                onChange={e => setForm(f => ({ ...f, education: { ...f.education, college_name: e.target.value } }))} />
            </div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:6 }}>
              <select className="glow-input" style={{ ...inp(), flex:1, minWidth:140 }}
                value={form.education?.degree_status||""}
                onChange={e => setForm(f => ({ ...f, education: { ...f.education, degree_status: e.target.value, degree_year: "", degree_sem: "" } }))}>
                <option value="">Status</option>
                <option value="completed">Completed ✔</option>
                <option value="pursuing">Pursuing ⏳</option>
              </select>
              {form.education?.degree_status === "completed" && (
                <select className="glow-input" style={{ ...inp(), minWidth:110 }}
                  value={form.education?.degree_year||""}
                  onChange={e => setForm(f => ({ ...f, education: { ...f.education, degree_year: e.target.value } }))}>
                  <option value="">Pass Year</option>
                  {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              )}
              {form.education?.degree_status === "pursuing" && (
                <select className="glow-input" style={{ ...inp(), flex:1, minWidth:100 }}
                  value={form.education?.degree_sem||""}
                  onChange={e => setForm(f => ({ ...f, education: { ...f.education, degree_sem: e.target.value } }))}>
                  <option value="">Year</option>
                  {["1st","2nd","3rd","4th"].map(y => <option key={y} value={y}>{y} Year</option>)}
                </select>
              )}
            </div>
          </div>

          <div className="db-save-row">
            <button onClick={save} disabled={saving} className="orange-btn"
              style={{ flex:1, padding:12, background:"#f97316", color:"#fff", border:"none", borderRadius:10, fontSize:14, cursor:"pointer", fontWeight:700 }}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={() => { setEditing(false); setErrs({}); }} className="ghost-btn"
              style={{ flex:1, padding:12, background:"transparent", color:"#64748b", border:"1px solid #1e293b", borderRadius:10, fontSize:14, cursor:"pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Referral ── */}
      {referral && (
        <div className="fade-in" style={{ background:"#111111", borderRadius:14, padding:16, marginBottom:14, border:"1px solid #f9731633" }}>
          <div style={{ fontWeight:700, color:"#f97316", fontSize:14, marginBottom:4 }}>🔗 Refer a Teacher</div>
          <div style={{ fontSize:12, color:"#64748b", marginBottom:14 }}>
            Share your link and help more teachers get discovered on PadhaiPass.
          </div>

          <div style={{ background:"#0a0a0a", borderRadius:8, padding:"10px 14px", border:"1px solid #1e293b", marginBottom:10, display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
            <span style={{ fontSize:12, color:"#e2e8f0", fontFamily:"monospace", wordBreak:"break-all" }}>
              {referral.referral_link}
            </span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(referral.referral_link).then(() => {
                  setRefCopied(true);
                  setTimeout(() => setRefCopied(false), 2000);
                });
              }}
              style={{ padding:"6px 14px", background: refCopied ? "#22c55e22" : "#f9731620", color: refCopied ? "#22c55e" : "#f97316", border:"1px solid " + (refCopied ? "#22c55e44" : "#f9731644"), borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0, transition:"all .2s" }}>
              {refCopied ? "✓ Copied!" : "Copy Link"}
            </button>
          </div>

          <div style={{ fontSize:13, color:"#e2e8f0" }}>
            <span style={{ color:"#f97316", fontWeight:700 }}>{referral.referral_count}</span>
            {" "}teacher{referral.referral_count !== 1 ? "s" : ""} referred
            {referral.referral_count > 0 && (
              <span style={{ color:"#22c55e", marginLeft:10, fontWeight:600 }}>
                · +{referral.referral_count} credit{referral.referral_count !== 1 ? "s" : ""} earned
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Stats ── */}
      <div className="fade-in db-stats-grid">
        {[
          [profile.ratings?.length > 0 ? (profile.ratings.reduce((a,b)=>a+b,0)/profile.ratings.length).toFixed(1)+"★" : "—", "Rating"],
          [approvedReviews.length,     "Reviews"],
          [profile.profile_views||0,   "Views"],
        ].map(([v,l]) => (
          <div key={l} className="stat-card" style={{ background:"#111111", borderRadius:12, padding:14, border:"1px solid #1e293b", textAlign:"center" }}>
            <div style={{ fontSize:20, fontWeight:800, color:"#f97316" }}>{v}</div>
            <div style={{ fontSize:11, color:"#e2e8f0", marginTop:4 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* ── Pending Reviews ── */}
      {pendingReviews.length > 0 && (
        <div className="fade-in" style={{ background:"#111111", borderRadius:14, padding:16, marginBottom:14, border:"1px solid #f9731633" }}>
          <div style={{ fontWeight:800, color:"#f97316", marginBottom:4, fontSize:16 }}>
            🔔 Student Verification Requests ({pendingReviews.length})
          </div>
          <div style={{ fontSize:13, color:"#e2e8f0", fontWeight:600, marginBottom:14 }}>
            Approve only students who actually studied with you.
          </div>
          {pendingReviews.map(r => (
            <div key={r._id} style={{ background:"#0a0a0a", borderRadius:10, padding:14, marginBottom:10, border:"1px solid #1e293b" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                <div style={{ fontWeight:600, fontSize:13 }}>{r.student?.name||"Student"}</div>
                <div style={{ fontSize:11, color:"#64748b" }}>{new Date(r.createdAt).toLocaleDateString("en-IN")}</div>
              </div>
              <div style={{ fontSize:12, color:"#e2e8f0", fontWeight:600, marginBottom:12, fontStyle:"italic" }}>
                Review content hidden — approve to publish, reject to remove
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={() => handleReview(r._id,"approved")}
                  style={{ flex:1, padding:"8px 0", background:"#22c55e15", color:"#22c55e", border:"1px solid #22c55e33", borderRadius:8, fontSize:12, cursor:"pointer", fontWeight:600 }}>
                  ✓ Yes, my student
                </button>
                <button onClick={() => handleReview(r._id,"rejected")}
                  style={{ flex:1, padding:"8px 0", background:"#ef444415", color:"#ef4444", border:"1px solid #ef444433", borderRadius:8, fontSize:12, cursor:"pointer", fontWeight:600 }}>
                  ✕ Not my student
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Approved Reviews ── */}
      {approvedReviews.length > 0 && (
        <div className="fade-in" style={{ background:"#111111", borderRadius:14, padding:16, marginBottom:14, border:"1px solid #1e293b" }}>
          <div style={{ fontWeight:800, color:"#f97316", marginBottom:14, fontSize:16 }}>
            Approved Reviews ({approvedReviews.length})
          </div>
          {approvedReviews.map(r => (
            <div key={r._id} style={{ borderBottom:"1px solid #1e293b", paddingBottom:10, marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <div style={{ fontWeight:600, fontSize:13 }}>{r.student?.name||"Student"}</div>
                <div style={{ fontSize:11, color:"#64748b" }}>{new Date(r.createdAt).toLocaleDateString("en-IN")}</div>
              </div>
              <p style={{ fontSize:13, color:"#64748b", margin:0, lineHeight:1.6 }}>{r.text}</p>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}